import React, { useEffect, useState } from "react";
import axios from "axios";
import "../style/dashboard.css";
import { useSidebar } from "../context/SidebarOpenContext";
import SummaryCard from "../component/dashboard/SummaryCard";
import { MdTableChart, MdBarChart } from "react-icons/md";
import RtsSummaryCard from "../component/dashboard/RtsSummaryCard";
import DailyReportGraphModal from "../component/dashboard/DailyReportGraphModel";
import DailyReportTableModal from "../component/dashboard/DailyReportTableModel";
import MonthlyReportGraphModal from "../component/dashboard/MonthlyReportGraphModel";
import MonthlyReportTableModal from "../component/dashboard/MonthlyReportTableModel";
import DailyProductionTableModal from "../component/dashboard/DailyProductionTableModel";
import DailyProductionGraphModal from "../component/dashboard/DailyProductionGraphModel";
import MonthlyProductionGraphModal from "../component/dashboard/MonthlyProductionGraphModel";
import MonthlyProductionTableModal from "../component/dashboard/MonthlyProductionTableModel";
import DailyBoxingTableModal from "../component/dashboard/DailyBoxingTableModel";
import DailyBoxingGraphModal from "../component/dashboard/DailyBoxingGraphModel";
import MonthlyBoxingGraphModal from "../component/dashboard/MonthlyBoxingGraphModel";
import MonthlyBoxingTableModal from "../component/dashboard/MonthlyBoxingTableModel";
import SectionActivities from "../component/dashboard/sectionActivities";
import IncompleteCuttingDcSection from "../component/dashboard/cuttingStock";
import RejectionSectionTabs from "../component/dashboard/rejections";

function Dashboard() {
  const { isSidebarOpen } = useSidebar();
  const [orderData, setOrderData] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState("All Orders");
  const [selectedSubOrder, setSelectedSubOrder] = useState("All Sub Orders");
  const [subOrderList, setSubOrderList] = useState([]);
  const selectedOrderData = orderData.find(
    (o) => o.order_id === Number(selectedOrder)
  );
  const [buyerList, setBuyerList] = useState([]);
  const [selectedBuyer, setSelectedBuyer] = useState("All Buyers");

  const [rtsSummary, setRtsSummary] = useState(null);
  const [totalQuantity, setTotalQuantity] = useState(0);
  const [monthlyTotal, setMonthlyTotal] = useState(0);
  const [avgPerDay, setAvgPerDay] = useState(0);

  const [productionSummary, setProductionSummary] = useState(null);
  const [prodTotalQuantity, setProdTotalQuantity] = useState(0);
  const [prodMonthlyTotal, setProdMonthlyTotal] = useState(0);
  const [prodAvgPerDay, setProdAvgPerDay] = useState(0);

  const [boxingSummary, setBoxingSummary] = useState(null);
  const [boxTotalQuantity, setBoxTotalQuantity] = useState(0);
  const [boxMonthlyTotal, setBoxMonthlyTotal] = useState(0);
  const [boxAvgPerDay, setBoxAvgPerDay] = useState(0);


  const [showGraph, setShowGraph] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [showMonthlyGraph, setShowMonthlyGraph] = useState(false);
  const [showMonthlyTable, setShowMonthlyTable] = useState(false);

  const [showProdGraph, setShowProdGraph] = useState(false);
  const [showProdTable, setShowProdTable] = useState(false);
  const [showMonthlyProdGraph, setShowMonthlyProdGraph] = useState(false);
  const [showMonthlyProdTable, setShowMonthlyProdTable] = useState(false);

  const [showBoxGraph, setShowBoxGraph] = useState(false);
  const [showBoxTable, setShowBoxTable] = useState(false);
  const [showMonthlyBoxGraph, setShowMonthlyBoxGraph] = useState(false);
  const [showMonthlyBoxTable, setShowMonthlyBoxTable] = useState(false);

  const [showSectionActivities, setShowSectionActivities] = useState(false);
  const [showCuttingStock, setShowCuttingStock] = useState(false);
  const [showRejections, setShowRejections] = useState(false);

  useEffect(() => {
    axios
      .get("dashboard/orders_with_suborder_ids")
      .then((res) => {
        if (res.data.success) {
          setOrderData(res.data.data);     // Set orders
          setBuyerList(res.data.buyers);   // Set all buyers
        }
      })
      .catch((err) => {
        console.error("Error fetching orders:", err);
      });
  }, []);


  useEffect(() => {
    const orderId = selectedOrder === "All Orders" ? "all" : selectedOrder;
    const subOrderId = selectedSubOrder === "All Sub Orders" ? "all" : selectedSubOrder;
    const buyerParam = selectedBuyer !== "All Buyers" ? `&buyer_id=${selectedBuyer}` : "";

    axios
      .get(`dashboard/get_production_summary?order_id=${orderId}&sub_order_id=${subOrderId}${buyerParam}`)
      .then((res) => {
        const data = res.data;
        setProductionSummary(data);

        let total = 0;
        let monthly = 0;
        const datesSet = new Set();
        const today = new Date();
        const currentMonth = today.getMonth();

        data.production_logs?.forEach((log) => {
          Object.values(log.size_variant_quantity || {}).forEach((qty) => {
            total += parseInt(qty);
          });

          const logMonth = new Date(log.date).getMonth();
          if (logMonth === currentMonth) {
            Object.values(log.size_variant_quantity || {}).forEach((qty) => {
              monthly += parseInt(qty);
            });
          }

          datesSet.add(log.date);
        });

        setProdTotalQuantity(total);
        setProdMonthlyTotal(monthly);
        setProdAvgPerDay(datesSet.size ? Math.round(total / datesSet.size) : 0);
      })
      .catch((err) => {
        console.error("Production summary fetch error:", err);
        setProductionSummary(null);
        setProdTotalQuantity(0);
        setProdMonthlyTotal(0);
        setProdAvgPerDay(0);
      });
  }, [selectedOrder, selectedSubOrder, selectedBuyer]); // ✅ Added selectedBuyer


  useEffect(() => {
    const orderId = selectedOrder === "All Orders" ? "all" : selectedOrder;
    const subOrderId = selectedSubOrder === "All Sub Orders" ? "all" : selectedSubOrder;
    const buyerParam = selectedBuyer !== "All Buyers" ? `&buyer_id=${selectedBuyer}` : "";

    const singleBoxingUrl = `dashboard/get_single_boxing_summary?order_id=${orderId}&sub_order_id=${subOrderId}${buyerParam}`;
    const groupBoxingUrl = `dashboard/get_group_boxing_summary?order_id=${orderId}&sub_order_id=${subOrderId}${buyerParam}`;

    Promise.all([axios.get(singleBoxingUrl), axios.get(groupBoxingUrl)])
      .then(([singleRes, groupRes]) => {
        const singleData = singleRes.data?.boxing_logs || [];
        const groupData = groupRes.data?.group_boxing_boxes || [];

        const mergedLogs = [
          ...singleData.map(log => ({ ...log, type: "single" })),
          ...groupData.map(log => ({ ...log, type: "group" }))
        ];

        const singleTotals = singleRes.data?.order_total_pieces || {};
        const groupTotals = groupRes.data?.order_total_pieces || {};

        const mergedTotals = { ...groupTotals };
        for (const [order, colors] of Object.entries(singleTotals)) {
          if (!mergedTotals[order]) mergedTotals[order] = {};
          for (const [color, value] of Object.entries(colors)) {
            mergedTotals[order][color] = (mergedTotals[order][color] || 0) + value;
          }
        }

        let total = 0;
        let monthly = 0;
        const datesSet = new Set();
        const today = new Date();
        const currentMonth = today.getMonth();

        mergedLogs.forEach(log => {
          const quantities = log.size_variant_quantity || {};
          Object.values(quantities).forEach(qty => {
            total += parseInt(qty);
          });

          const logMonth = new Date(log.date).getMonth();
          if (logMonth === currentMonth) {
            Object.values(quantities).forEach(qty => {
              monthly += parseInt(qty);
            });
          }

          datesSet.add(log.date);
        });

        setBoxingSummary({
          boxing_logs: mergedLogs,
          order_total_pieces: mergedTotals
        });

        setBoxTotalQuantity(total);
        setBoxMonthlyTotal(monthly);
        setBoxAvgPerDay(datesSet.size ? Math.round(total / datesSet.size) : 0);
      })
      .catch((err) => {
        console.error("Boxing summary fetch error:", err);
        setBoxingSummary(null);
        setBoxTotalQuantity(0);
        setBoxMonthlyTotal(0);
        setBoxAvgPerDay(0);
      });
  }, [selectedOrder, selectedSubOrder, selectedBuyer]);


  const handleBuyerChange = (e) => {
    const buyerId = e.target.value;
    setSelectedBuyer(buyerId);
    setSelectedOrder("All Orders");
    setSelectedSubOrder("All Sub Orders");
    setSubOrderList([]);
  };


  const handleOrderChange = (e) => {
    const value = e.target.value;
    setSelectedOrder(value);
    setSelectedSubOrder("All Sub Orders");

    if (value === "All Orders") {
      setSubOrderList([]);
    } else {
      const selected = orderData.find((o) => o.order_id === Number(value));
      setSubOrderList(selected?.suborder_ids || []);
    }
  };


  useEffect(() => {
    const orderId = selectedOrder === "All Orders" ? "all" : selectedOrder;
    const subOrderId = selectedSubOrder === "All Sub Orders" ? "all" : selectedSubOrder;

    // ✅ Add buyer_id only if specific buyer is selected
    const buyerParam = selectedBuyer !== "All Buyers" ? `&buyer_id=${selectedBuyer}` : "";

    axios
      .get(
        `dashboard/get_ready_to_sewing_summary?order_id=${orderId}&sub_order_id=${subOrderId}${buyerParam}`
      )
      .then((res) => {
        const data = res.data;
        setRtsSummary(data);

        let total = 0;
        let monthly = 0;
        const datesSet = new Set();
        const today = new Date();
        const currentMonth = today.getMonth();

        data.ready_to_sewing_logs?.forEach((log) => {
          Object.values(log.size_variant_quantity || {}).forEach((qty) => {
            total += parseInt(qty);
          });

          const logMonth = new Date(log.date).getMonth();
          if (logMonth === currentMonth) {
            Object.values(log.size_variant_quantity || {}).forEach((qty) => {
              monthly += parseInt(qty);
            });
          }

          datesSet.add(log.date);
        });

        setTotalQuantity(total);
        setMonthlyTotal(monthly);
        setAvgPerDay(datesSet.size ? Math.round(total / datesSet.size) : 0);
      })
      .catch((err) => {
        console.error("RTS summary fetch error:", err);
        setRtsSummary(null);
        setTotalQuantity(0);
        setMonthlyTotal(0);
        setAvgPerDay(0);
      });
  }, [selectedOrder, selectedSubOrder, selectedBuyer]); // ✅ Added selectedBuyer here


  const filteredOrders = selectedBuyer === "All Buyers"
    ? orderData
    : orderData.filter(order => order.buyer.customer_id === Number(selectedBuyer));

  return (
    <div className={`${isSidebarOpen ? "p-4 sm:ml-64 bg-content-bg min-h-screen" : "p-4 bg-content-bg min-h-screen"}`}>
      <div className="p-4 mt-10">
        <div className="flex flex-wrap justify-center sm:justify-end items-center gap-3 mb-4">
          <button
            className="bg-gray-700 text-white px-4 sm:px-6 py-2 rounded text-base sm:text-lg"
            onClick={() => {
              setShowSectionActivities((prev) => {
                const newValue = !prev;
                setShowCuttingStock(false);
                setShowRejections(false);
                return newValue;
              });
            }}
          >
            {showSectionActivities ? "Hide Section Activities" : "Section Activities"}
          </button>

          <button
            className="bg-gray-700 text-white px-4 sm:px-6 py-2 rounded text-base sm:text-lg"
            onClick={() => {
              setShowCuttingStock((prev) => {
                const newValue = !prev;
                setShowSectionActivities(false);
                setShowRejections(false);
                return newValue;
              });
            }}
          >
            {showCuttingStock ? "Hide Cutting Stock" : "Cutting Stock"}
          </button>

          <button
            className="bg-gray-700 text-white px-4 sm:px-6 py-2 rounded text-base sm:text-lg"
            onClick={() => {
              setShowRejections((prev) => {
                const newValue = !prev;
                setShowSectionActivities(false);
                setShowCuttingStock(false);
                return newValue;
              });
            }}
          >
            {showRejections ? "Hide Rejections" : "Rejections"}
          </button>
        </div>


        {/* Top Level Section Activities Display */}
        {showSectionActivities && (
          <div className="mb-6">
            <SectionActivities />
          </div>
        )}


        {!showSectionActivities && (
          <div className="dashboard-container overflow-x-hidden">
            {/* Dropdowns */}
            <div className="dropdown-wrapper flex flex-wrap gap-6  items-end">
              {/* Buyer Dropdown */}
              <div className="flex flex-col flex-1 min-w-[200px]">
                <label htmlFor="buyer-select" className="text-sm font-medium text-gray-700 mb-1">Buyer</label>
                <select
                  id="buyer-select"
                  value={selectedBuyer}
                  onChange={handleBuyerChange}
                  className="px-3 py-2 rounded-md border border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="All Buyers">All Buyers</option>
                  {buyerList.map((buyer) => (
                    <option key={buyer.customer_id} value={buyer.customer_id}>
                      {buyer.customer_name} ({buyer.customer_no})
                    </option>
                  ))}
                </select>
              </div>

              {/* Order Dropdown */}
              <div className="flex flex-col flex-1 min-w-[200px]">
                <label htmlFor="order-select" className="text-sm font-medium text-gray-700 mb-1">Order ID</label>
                <select
                  id="order-select"
                  value={selectedOrder}
                  onChange={handleOrderChange}
                  className="px-3 py-2 rounded-md border border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="All Orders">All Orders</option>
                  {orderData
                    .filter(order => selectedBuyer === "All Buyers" || order.buyer.customer_id === Number(selectedBuyer))
                    .map(order => (
                      <option key={order.order_id} value={order.order_id}>
                        {order.cut_order_no}
                      </option>
                    ))}
                </select>
              </div>

              {/* Suborder Dropdown */}
              <div className="flex flex-col flex-1 min-w-[200px]">
                <label htmlFor="suborder-select" className="text-sm font-medium text-gray-700 mb-1">Color Way</label>
                <select
                  id="suborder-select"
                  value={selectedSubOrder}
                  onChange={(e) => setSelectedSubOrder(e.target.value)}
                  className="px-3 py-2 rounded-md border border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="All Sub Orders">All Color Ways</option>
                  {subOrderList.map((subId, index) => {
                    const color = selectedOrderData?.suborder_colours?.[index] || "Color";
                    return (
                      <option key={subId} value={subId}>
                        {color}
                      </option>
                    );
                  })}
                </select>

              </div>
            </div>
            {showCuttingStock && (
              <div className="">
                <IncompleteCuttingDcSection
                  selectedBuyer={selectedBuyer}
                  selectedOrder={selectedOrder}
                  selectedSubOrder={selectedSubOrder}
                />
              </div>
            )}

            {showRejections && (
              <div className="">
                <RejectionSectionTabs
                  selectedBuyer={selectedBuyer}
                  selectedOrder={selectedOrder}
                  selectedSubOrder={selectedSubOrder}
                />
              </div>
            )}

            {/* Summary Cards */}
            {filteredOrders.length > 0 ? (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-4">
                  {selectedOrder === "All Orders" && selectedSubOrder === "All Sub Orders" && (
                    <SummaryCard title="Total Orders" value={filteredOrders.length} variant="primary" />
                  )}

                  {selectedOrder !== "All Orders" && selectedOrderData && (
                    <>
                      <SummaryCard
                        title="Color Ways"
                        value={subOrderList.length}
                        colors={selectedOrderData?.suborder_colours || []}
                        variant="secondary"
                      />
                      <SummaryCard
                        title="Order No."
                        value={selectedOrderData?.cut_order_no || "-"}
                        variant="info"
                      />
                      <SummaryCard
                        title="Total Pieces"
                        value={selectedOrderData?.total_piece || 0}
                        variant="info"
                      />
                    </>
                  )}

                  {selectedOrder !== "All Orders" && selectedSubOrder !== "All Sub Orders" && (() => {
                    const detail = selectedOrderData?.suborder_details?.find(
                      (sub) => sub.sub_order_id === Number(selectedSubOrder)
                    );
                    return (
                      detail && (
                        <SummaryCard
                          title={`Color Way #${detail.sub_order_id}`}
                          value={detail.colour}
                          variant="secondary"
                          details={[{ label: "Total Pieces", value: detail.total_piece }]}
                        />
                      )
                    );
                  })()}
                </div>


                {/* RTS Section */}
                {rtsSummary && (
                  <>
                    <h3 className="text-xl font-semibold text-gray-700 mt-4">
                      {selectedOrder === "All Orders" ? "Ready to Sewing (All Orders)" : "Ready to Sewing"}
                    </h3>
                    <div className="summary-row flex flex-wrap gap-4 overflow-x-auto max-w-full p-3">
                      <RtsSummaryCard
                        title="Daily Report"
                        value={totalQuantity}
                        icons={[
                          { icon: <MdBarChart size={20} />, onClick: () => setShowGraph(true), title: "View Chart" },
                          { icon: <MdTableChart size={20} />, onClick: () => setShowTable(true), title: "View Table" },
                        ]}
                      />
                      <RtsSummaryCard
                        title="Monthly Report"
                        value={monthlyTotal}
                        icons={[
                          { icon: <MdBarChart size={20} />, onClick: () => setShowMonthlyGraph(true), title: "View Chart" },
                          { icon: <MdTableChart size={20} />, onClick: () => setShowMonthlyTable(true), title: "View Table" },
                        ]}
                      />
                      <RtsSummaryCard title="Average/Day" value={avgPerDay} />
                    </div>
                  </>
                )}

                {/* Production Section */}
                {productionSummary && (
                  <>
                    <h3 className="text-xl font-semibold text-gray-700 mt-6">
                      {selectedOrder === "All Orders" ? "Production (All Orders)" : "Production"}
                    </h3>
                    <div className="summary-row flex flex-wrap gap-4 overflow-x-auto max-w-full p-3">
                      <RtsSummaryCard
                        title="Daily Report"
                        value={prodTotalQuantity}
                        icons={[
                          { icon: <MdBarChart size={20} />, onClick: () => setShowProdGraph(true), title: "View Chart" },
                          { icon: <MdTableChart size={20} />, onClick: () => setShowProdTable(true), title: "View Table" },
                        ]}
                      />
                      <RtsSummaryCard
                        title="Monthly Report"
                        value={prodMonthlyTotal}
                        icons={[
                          { icon: <MdBarChart size={20} />, onClick: () => setShowMonthlyProdGraph(true), title: "View Chart" },
                          { icon: <MdTableChart size={20} />, onClick: () => setShowMonthlyProdTable(true), title: "View Table" },
                        ]}
                      />
                      <RtsSummaryCard title="Average/Day" value={prodAvgPerDay} />
                    </div>
                  </>
                )}

                {boxingSummary && (
                  <>
                    <h3 className="text-xl font-semibold text-gray-700 mt-6">
                      {selectedOrder === "All Orders" ? "Boxing (All Orders)" : "Boxing"}
                    </h3>
                    <div className="summary-row flex flex-wrap gap-4 overflow-x-auto max-w-full p-3">
                      <RtsSummaryCard
                        title="Daily Report"
                        value={boxTotalQuantity}
                        icons={[
                          { icon: <MdBarChart size={20} />, onClick: () => setShowBoxGraph(true), title: "View Daily Chart" },
                          { icon: <MdTableChart size={20} />, onClick: () => setShowBoxTable(true), title: "View Daily Table" },
                        ]}
                      />
                      <RtsSummaryCard
                        title="Monthly Report"
                        value={boxMonthlyTotal}
                        icons={[
                          { icon: <MdBarChart size={20} />, onClick: () => setShowMonthlyBoxGraph(true), title: "View Monthly Chart" },
                          { icon: <MdTableChart size={20} />, onClick: () => setShowMonthlyBoxTable(true), title: "View Monthly Table" },
                        ]}
                      />
                      <RtsSummaryCard title="Average/Day" value={boxAvgPerDay} />
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="p-6 text-gray-500 font-medium text-lg">
                No orders available for this buyer.
              </div>
            )}
          </div>
        )}
      </div>

      {showGraph && (
        <DailyReportGraphModal
          data={rtsSummary?.ready_to_sewing_logs}
          orderTotals={rtsSummary?.order_total_pieces || {}}
          onClose={() => setShowGraph(false)}
        />
      )}

      {showTable && (
        <DailyReportTableModal
          data={rtsSummary?.ready_to_sewing_logs}
          onClose={() => setShowTable(false)}
          orderTotals={rtsSummary?.order_total_pieces || {}}
        />
      )}

      {showMonthlyGraph && (
        <MonthlyReportGraphModal
          data={rtsSummary?.ready_to_sewing_logs}
          orderTotals={rtsSummary?.order_total_pieces}
          sizeSummary={rtsSummary?.overall_size_summary}
          onClose={() => setShowMonthlyGraph(false)}
        />

      )}

      {showMonthlyTable && (
        <MonthlyReportTableModal
          data={rtsSummary?.ready_to_sewing_logs}
          onClose={() => setShowMonthlyTable(false)}
          orderTotals={rtsSummary?.order_total_pieces || {}}
        />
      )}

      {showProdGraph && (
        <DailyProductionGraphModal
          data={productionSummary?.production_logs}
          orderTotals={productionSummary?.order_total_pieces}
          rejectionData={productionSummary?.productionRejections}
          onClose={() => setShowProdGraph(false)}
        />
      )}

      {showProdTable && (
        <DailyProductionTableModal
          data={productionSummary?.production_logs}
          onClose={() => setShowProdTable(false)}
          orderTotals={productionSummary?.order_total_pieces}
        />
      )}

      {showMonthlyProdGraph && (
        <MonthlyProductionGraphModal
          data={productionSummary?.production_logs}
          orderTotals={productionSummary?.order_total_pieces}
          sizeSummary={productionSummary?.overall_size_summary}
          onClose={() => setShowMonthlyProdGraph(false)}
        />
      )}

      {showMonthlyProdTable && (
        <MonthlyProductionTableModal
          data={productionSummary?.production_logs}
          onClose={() => setShowMonthlyProdTable(false)}
          orderTotals={productionSummary?.order_total_pieces}
          sizeSummary={productionSummary?.overall_size_summary}
        />
      )}

      {showBoxGraph && (
        <DailyBoxingGraphModal
          data={boxingSummary?.boxing_logs || []}
          orderTotals={boxingSummary?.order_total_pieces || {}}
          onClose={() => setShowBoxGraph(false)}
        />
      )}


      {showBoxTable && (
        <DailyBoxingTableModal
          data={boxingSummary}
          orderTotals={boxingSummary?.order_total_pieces || {}}
          onClose={() => setShowBoxTable(false)}
        />
      )}

      {showMonthlyBoxGraph && (
        <MonthlyBoxingGraphModal
          data={boxingSummary?.boxing_logs || []}
          orderTotals={boxingSummary?.order_total_pieces || {}}
          onClose={() => setShowMonthlyBoxGraph(false)}
        />
      )}

      {showMonthlyBoxTable && (
        <MonthlyBoxingTableModal
          data={boxingSummary}
          onClose={() => setShowMonthlyBoxTable(false)}
        />
      )}
    </div>
  );
}

export default Dashboard;